import React, {memo, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import {getToggleSidebar} from "reduxStore/reducers/toggleSidebar";
import Arrow from "assets/images/arrow-select.svg";
import Sentinel from "assets/images/sentinel.svg";
import {useTranslation} from "react-i18next";
import {appUrls} from "../constants/appUrls";
import {sidebarIcon, sidebarItemTypes} from "./sidebarItemTypes";
import {useGetMenuInformerQuery, useGetMenuListQuery} from "../redux/api/MenuApi";
import {Load} from "../components/load/Load";
import {useGetTenancyListQuery} from "../redux/api/TenancyApi";
import {getLogoUrl} from "../utils/getLogoUrl";
import classNames from "classnames";

const SidebarElemHasSub = memo(({type, setShowSub, showSub, marker}) => {
  const {isOpen} = useSelector(getToggleSidebar);
  const {t} = useTranslation("sidebar");
  let {pathname} = useLocation();
  const isActive = pathname.includes(appUrls[type] + "/")

  return (
    <li className={`border-top border-light cursor-pointer`}
        onClick={() => setShowSub(!showSub)}
    >
      <div className={`sidebar-link text-nowrap d-flex w-100 align-items-center ${isActive ? "active" : ""}`}>
        <div className="sidebar-icon py-3">
          {sidebarIcon[type]}
          {!isOpen && <div className={"d-flex pt-3 justify-content-center align-items-center w-100"}>
            <img className={`rotateArrow ${showSub ? "" : "active"}`} src={Arrow} alt={"show submenu"}/>
          </div>}
        </div>
        <div className={`text-nowrap sidebar-text d-flex align-items-center`}>
          <div style={{lineHeight: "20px"}}>{t(type)}</div>
          {marker ? marker() : null}
        </div>
        <div className={`px-3 ${isOpen ? " " : "d-none"}`}>
          <img className={`rotateArrow ${showSub ? "" : "active"}`} src={Arrow} alt={"show submenu"}/>
        </div>
      </div>
    </li>
  )
});

const SidebarElemLink = memo(({type, marker, className, classForSub = ""}) => {
  let {pathname} = useLocation();
  const {t} = useTranslation("sidebar");

  return (
    <li key={type} className={`${className} border-top border-light`}>
      <Link
        className={`sidebar-link text-nowrap d-flex w-100 py-3 align-items-center ${
          pathname.includes(appUrls[type]) ? "active" : ""
        }`}
        to={appUrls[type]}
      >
        <div className="sidebar-icon">
          {sidebarIcon[type]}
        </div>
        <div
          className={`text-nowrap sidebar-text d-flex align-items-center ${classForSub}`}
        >
          <div style={{lineHeight: "20px"}}>{t(type)}</div>
          {marker ? marker() : null}
        </div>
      </Link>
    </li>
  )
});

const SubLinkElement = memo(({subLinks, showSub}) => {
  const {data: sidebarInfo} = useGetMenuInformerQuery();
  const {pathname} = useLocation();

  return (<ul className={"navbar-nav flex-wrap"}>{subLinks.map(({type, hasMarker}) => {
    return <SidebarElemLink
      key={type}
      type={type}
      marker={hasMarker ? () => <Marker oldNotes={sidebarInfo?.[type + "All"]}
                                        newNotes={sidebarInfo?.[type + "New"]}/> : null}
      classForSub={"pl-1"}
      className={classNames("children-link",
        {
          "hidden": !showSub,
          "active": pathname.includes(appUrls[type]),
        }
      )}
    />
  })}</ul>)
})

const LinkElement = memo(({type, marker, subLinks, show = false}) => {
  const [showSub, setShowSub] = useState(show);
  return (<>{
      subLinks ? <>
        <SidebarElemHasSub type={type} setShowSub={setShowSub} showSub={showSub} marker={marker}/>
        <SubLinkElement subLinks={subLinks} showSub={showSub}/>
      </> : <SidebarElemLink type={type} marker={marker} className={''}/>
    }</>
  )
});

const Marker = memo(({newNotes = "0", oldNotes = "0"}) => {
  return (<>
    {(+newNotes !== 0 || +oldNotes !== 0) &&
      <div className={`d-block sidebar-marker-wrap ml-2 `}>
        {+oldNotes !== 0 && <span className={`sidebar-marker ${+newNotes === 0 ? "pr-1" : ""}`}>{oldNotes}</span>}
        {+newNotes !== 0 && <span className={"sidebar-marker active"}>{newNotes} {"new"} </span>}
      </div>
    }</>)
});

const Sidebar = () => {
  const {isOpen} = useSelector(getToggleSidebar);
  const {pathname} = useLocation();
  const {isLoading: tenantListLoading} = useGetTenancyListQuery();

  const {data, isLoading} = useGetMenuListQuery(undefined, {skip: tenantListLoading});
  const {
    data: sidebarInfo,
    isLoading: isInformerLoading
  } = useGetMenuInformerQuery(undefined, {skip: isLoading || tenantListLoading})

  return (
    <div className={`sidebar shadow ${isOpen ? "open" : ""}`}>
      <Link
        to={appUrls.clientList}
        className="d-flex justify-content-center align-items-center admin-sidebar p-2 mt-2 mb-3"
        style={{height: "40px"}}
      >
        {isOpen ? "Administration" : ""}
      </Link>
      {(isLoading || isInformerLoading) && <Load/>}

      <div className="" style={{height: "calc(100vh - 100px)", overflow: "auto"}}>

        <ul className="navbar-nav flex-wrap">
          <>{
            data?.map((item) => {
              if (item.type === sidebarItemTypes.sentinel) {
                return <li key={item.type} className={`border-top border-light`}>
                  <a
                    className={`sidebar-link text-nowrap d-flex w-100 py-3 align-items-center`}
                    href={`https://${window.location.host}/apps/sentinel/`}
                    target={"_self"}
                  >
                    <div className="sidebar-icon">
                      <img src={Sentinel} alt=" " width={18} height={18}/>
                    </div>
                    <div className={`text-nowrap sidebar-text d-flex align-items-center`}>
                      <div style={{lineHeight: "20px"}}>{"Sentinel"}</div>
                    </div>
                  </a>
                </li>
              }
              return <LinkElement
                key={item.type}
                type={item.type}
                show={pathname.includes(appUrls[item.type])}
                marker={item.hasMarker ? () => <Marker oldNotes={sidebarInfo?.[item.type + "All"]}
                                                       newNotes={sidebarInfo?.[item.type + "New"]}/> : null}
                subLinks={item.subItems || null}
              />
            })
          }</>

        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
